import { useInfiniteQuery } from '@tanstack/react-query';
import { operations } from './generated/couponsApi';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { COUPONS_URLS } from '@/constants/api';
import { apiCouponsFetcher } from '../fetcher';
import { useGetCmsEshopsLogosQuery } from '../sanity/generated';
import { Maybe } from '@/utils/types';
import { sanityLanguage } from '@/constants/language';

export type CouponsListResponseBase =
	operations['couponsList']['responses']['200']['content']['application/json'];

export type CouponsListResponseBaseArray = Array<
	CouponsListResponseBase['data'][number] & {
		merchant: CouponsListResponseBase['data'][number]['merchant'] & {
			logoUrl: Maybe<string>;
		};
		imageUrl: string;
		linkToStore: string;
	}
>;

export type CouponsListResponse = CouponsListResponseBase & {
	data: CouponsListResponseBaseArray;
};

type QueryParams = operations['couponsList']['parameters']['query'];

export function useCouponsList(
	params: QueryParams,
	{
		enabled,
	}: {
		enabled: boolean;
	}
) {
	return useInfiniteQuery({
		queryKey: QUERY_KEYS.couponsList(params),
		queryFn: async ({ pageParam }) => {
			const res = await apiCouponsFetcher<Promise<CouponsListResponse>>(COUPONS_URLS.couponsList, {
				searchParams: { ...{ ...params }, ...{ ...pageParam } },
			});

			const data = await res.json;

			const merchantNames =
				[...new Set(data?.data.map((coupon) => coupon.merchant.name))].filter(Boolean) || [];

			if (!merchantNames) {
				return new Promise<CouponsListResponse | undefined>((resolve) => {
					return resolve(data);
				});
			}

			const cmsResponse = await useGetCmsEshopsLogosQuery.fetcher({
				merchantNames,
				i18nLang: sanityLanguage,
			})();

			const result = data?.data.map((coupon) => {
				const matchingEshop = cmsResponse.allEshops.find(
					(eshop) => eshop.eshopApiId === coupon.merchant.name
				);

				return {
					...coupon,
					merchant: {
						...coupon.merchant,
						logoUrl: matchingEshop?.logo?.asset?.url,
					},
					imageUrl: matchingEshop?.heroImage?.asset?.url || coupon.imageUrl,
					linkToStore: matchingEshop?.slug?.current || '',
				} as CouponsListResponse['data'][number];
			});

			return new Promise<CouponsListResponse | undefined>((resolve) => {
				if (!data) {
					return resolve(undefined);
				}
				const mergedData = {
					...data,
					data: result || [],
				};

				return resolve(mergedData);
			});
		},
		getNextPageParam: (lastPage: CouponsListResponse | undefined) => {
			if (!lastPage?.pagingInfo.nextPage) {
				return undefined;
			}

			const url = new URLSearchParams(
				new URL(`${process.env.NEXT_PUBLIC_API_URL}${lastPage?.pagingInfo.nextPage}`).searchParams
			);

			const after = url.get('after');
			const before = url.get('before') || '';

			return {
				after,
				before,
			};
		},
		getPreviousPageParam: (firstPage: CouponsListResponse | undefined) => {
			if (!firstPage?.pagingInfo.prevPage) {
				return undefined;
			}

			const before = new URLSearchParams(
				new URL(`${process.env.NEXT_PUBLIC_API_URL}${firstPage?.pagingInfo.prevPage}`).searchParams
			).get('before');

			return before ? { before } : undefined;
		},
		initialPageParam: undefined,
		enabled,
	});
}
